(function () {
  'use strict';

  if (customElements.get('poi-map')) return;

  const PIN_ICON_URL = 'https://cdn.shopify.com/s/files/1/0901/1465/4510/files/map-pin.png?v=1748633362';
  const FEATURED_LOGO_URL = 'https://cdn.shopify.com/s/files/1/0901/1465/4510/files/BGE-Logo_hori_light-BG.png?v=1750786151';
  const FILTERED_MAX_ZOOM = 14;

  const ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

  let mapsApiPromise = null;
  let callbackCount = 0;

  function loadMapsApi(apiKey) {
    if (window.google && window.google.maps) {
      return Promise.resolve();
    }

    if (!mapsApiPromise) {
      mapsApiPromise = new Promise((resolve, reject) => {
        const callbackName = '__poiMapApiLoaded' + callbackCount++;
        window[callbackName] = () => {
          delete window[callbackName];
          resolve();
        };

        const script = document.createElement('script');
        script.src =
          'https://maps.googleapis.com/maps/api/js?key=' +
          encodeURIComponent(apiKey) +
          '&loading=async&callback=' +
          callbackName;
        script.async = true;
        script.defer = true;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    return mapsApiPromise;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ESCAPE_MAP[char]);
  }

  function uniqueSorted(values) {
    return Array.from(new Set(values.filter(Boolean))).sort();
  }

  class PoiMapElement extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.apiKey = this.dataset.apiKey;
      if (!this.apiKey) {
        console.warn('poi-map: no Google Maps API key resolved (shop metafield custom.google_maps_api_key is empty).');
        return;
      }

      this.locations = this.readLocations();
      this.markers = [];

      if (!this.locations.length) return;

      loadMapsApi(this.apiKey)
        .then(() => this.initMap())
        .catch((error) => console.error('poi-map: failed to load Google Maps.', error));
    }

    readLocations() {
      const script = this.querySelector('[data-poi-map-locations]');
      if (!script) return [];

      try {
        const data = JSON.parse(script.textContent);
        return data.filter((poi) => poi && Number.isFinite(poi.lat) && Number.isFinite(poi.lng));
      } catch (error) {
        console.error('poi-map: could not parse location data.', error);
        return [];
      }
    }

    initMap() {
      const canvas = this.querySelector('.poi-map__canvas');
      if (!canvas) return;

      const center = {
        lat: parseFloat(this.dataset.centerLat),
        lng: parseFloat(this.dataset.centerLng),
      };
      const zoom = parseInt(this.dataset.zoom, 10) || 4;

      this.map = new google.maps.Map(canvas, { center, zoom });
      this.homeView = { center, zoom };

      this.locations.forEach((poi) => this.addMarker(poi));

      if (this.dataset.autoFit === 'true' && this.markers.length) {
        this.fitToMarkers(
          this.markers.map(({ marker }) => marker),
          { updateHomeView: true }
        );
      }

      if (this.dataset.enableFilters === 'true') {
        this.initFilters();
      }
    }

    addMarker(poi) {
      const marker = new google.maps.Marker({
        position: { lat: poi.lat, lng: poi.lng },
        map: this.map,
        title: poi.name,
        icon: {
          url: PIN_ICON_URL,
          scaledSize: new google.maps.Size(20, 20),
        },
      });

      const directionsUrl =
        poi.linkUrl ||
        'https://www.google.com/maps/dir/?api=1&destination=' +
          encodeURIComponent(poi.address || poi.name || '');

      const infoWindow = new google.maps.InfoWindow({
        content:
          '<div class="poi-map__info">' +
          (poi.isFeatured
            ? '<img src="' + escapeHtml(FEATURED_LOGO_URL) + '" alt="Big Green Egg" class="poi-map__info-logo">'
            : '') +
          '<strong>' + escapeHtml(poi.name) + '</strong><br>' +
          escapeHtml(poi.address || '') + '<br>' +
          '<a href="' + escapeHtml(directionsUrl) + '" target="_blank" rel="noopener" class="btn btn--solid btn--primary poi-map__info-link">' +
          escapeHtml(poi.linkLabel || 'Get Directions') +
          '</a>' +
          '</div>',
      });

      marker.addListener('click', () => infoWindow.open(this.map, marker));

      this.markers.push({ marker, poi });
    }

    // Clamps zoom after fitBounds so a single/tightly-clustered marker doesn't over-zoom.
    fitToMarkers(markers, { updateHomeView = false } = {}) {
      const bounds = new google.maps.LatLngBounds();
      markers.forEach((marker) => bounds.extend(marker.getPosition()));

      this.map.fitBounds(bounds);
      google.maps.event.addListenerOnce(this.map, 'idle', () => {
        if (this.map.getZoom() > FILTERED_MAX_ZOOM) this.map.setZoom(FILTERED_MAX_ZOOM);

        if (updateHomeView) {
          this.homeView = { center: this.map.getCenter().toJSON(), zoom: this.map.getZoom() };
        }
      });
    }

    // City (data-depends-on) starts disabled/empty until State is touched — matches
    // assets/custom.js's metaobject-grid filter convention.
    initFilters() {
      this.stateSelect = this.querySelector('[data-filter-field="state"]');
      this.citySelect = this.querySelector('[data-filter-field="city"]');
      if (!this.stateSelect || !this.citySelect) return;

      const states = uniqueSorted(this.locations.map((poi) => poi.state));
      this.appendOptions(this.stateSelect, states);

      this.stateSelect.addEventListener('change', () => {
        this.refreshCityOptions();
        this.applyFilter();
      });
      this.citySelect.addEventListener('change', () => this.applyFilter());
    }

    refreshCityOptions() {
      const state = this.stateSelect.value;
      const cities = uniqueSorted(
        this.locations.filter((poi) => !state || poi.state === state).map((poi) => poi.city)
      );

      this.citySelect.innerHTML = '<option value="">' + (this.citySelect.dataset.allLabel || 'Any') + '</option>';
      this.appendOptions(this.citySelect, cities);
      this.citySelect.value = '';
      this.citySelect.disabled = cities.length === 0;
    }

    appendOptions(select, values) {
      select.insertAdjacentHTML(
        'beforeend',
        values.map((value) => '<option value="' + escapeHtml(value) + '">' + escapeHtml(value) + '</option>').join('')
      );
    }

    applyFilter() {
      const selectedState = this.stateSelect.value;
      const selectedCity = this.citySelect.value;
      const visibleMarkers = [];

      this.markers.forEach(({ marker, poi }) => {
        const match = (!selectedState || poi.state === selectedState) && (!selectedCity || poi.city === selectedCity);

        marker.setVisible(match);
        if (match) visibleMarkers.push(marker);
      });

      if (!selectedState && !selectedCity) {
        this.map.setCenter(this.homeView.center);
        this.map.setZoom(this.homeView.zoom);
        return;
      }

      if (visibleMarkers.length) this.fitToMarkers(visibleMarkers);
    }
  }

  customElements.define('poi-map', PoiMapElement);
})();
